import React from 'react';
import { Link } from 'react-router-dom';
import { FREE_PM_CHECKLISTS_URL, SALES_MAILTO } from '../lib/publicSite';
import { 
  CheckCircle2, Clock, Users, TrendingUp, 
  BarChart3, MessageSquare, ClipboardCheck, 
  Calendar, Shield, Zap, ArrowRight
} from 'lucide-react';

const LandingPagePreview = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-amber-500 rounded-sm flex items-center justify-center">
                <Zap className="w-6 h-6 text-black" />
              </div>
              <span className="text-xl font-bold text-white">WorkStream Pro</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-slate-300 hover:text-white px-4 py-2">
                Sign In
              </Link>
              <Link 
                to="/signup" 
                className="bg-amber-500 hover:bg-amber-600 text-black px-6 py-2 rounded-sm font-medium"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Run your facility with <span className="text-amber-500">confidence</span>
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Streamline facility maintenance, from request to resolution. Reduce unplanned downtime and respond faster to unwelcome surprises.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/signup" 
                  className="bg-amber-500 hover:bg-amber-600 text-black px-8 py-4 rounded-sm font-bold text-lg inline-flex items-center gap-2"
                >
                  Sign up for free
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button className="border-2 border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-black px-8 py-4 rounded-sm font-bold text-lg">
                  Schedule Demo
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop" 
                alt="Facility management" 
                className="rounded-lg w-full h-64 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop" 
                alt="Work order tracking" 
                className="rounded-lg w-full h-64 object-cover mt-8"
              />
              <img 
                src="https://images.unsplash.com/photo-1504384764586-bb4cdc1707b0?w=600&h=400&fit=crop" 
                alt="Team collaboration" 
                className="rounded-lg w-full h-64 object-cover"
              />
              <img 
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop" 
                alt="Real-time updates" 
                className="rounded-lg w-full h-64 object-cover mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Logos */}
      <section className="py-12 bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-slate-400 mb-8">Included in the workspace</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ['Work orders', 'Create, assign, and close jobs.'],
              ['Locations', 'Building, floor, then place.'],
              ['Inventory', 'Quantity on hand and a reorder point.'],
            ].map(([title, copy]) => (
              <div key={title} className="border border-slate-800 rounded-lg p-5">
                <h3 className="font-semibold text-white mb-2">{title}</h3>
                <p className="text-sm text-slate-400">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            What the workspace includes
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              ['Work orders', 'Open, assign, and close jobs'],
              ['PM schedules', 'Repeat work on a calendar date'],
              ['Parts', 'Stock and reorder points'],
              ['Reports', 'Open, assigned, and completed work'],
            ].map(([title, copy]) => (
              <div key={title} className="bg-slate-900 border border-slate-800 rounded-lg p-8 text-center">
                <div className="text-xl font-bold text-amber-500 mb-2">{title}</div>
                <p className="text-slate-300">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto space-y-32">
          {/* Feature 1 */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold mb-6">
                Keep track of work across your facility
              </h3>
              <p className="text-xl text-slate-300 mb-6">
                Record all your maintenance work in one place. Get real-time status updates on your phone, know exactly what's been done and what hasn't, and access detailed work history for all of your assets.
              </p>
              <button className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2">
                Learn more
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-slate-800 rounded-lg p-8 h-96 flex items-center justify-center">
              <BarChart3 className="w-32 h-32 text-amber-500" />
            </div>
          </div>

          {/* Feature 2 */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-slate-800 rounded-lg p-8 h-96 flex items-center justify-center">
              <ClipboardCheck className="w-32 h-32 text-amber-500" />
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="text-4xl font-bold mb-6">
                Simplify work requests
              </h3>
              <p className="text-xl text-slate-300 mb-6">
                Receive work requests from co-workers, customers and more, in the format you choose. Prioritize them quickly, and create work orders automatically to speed up the maintenance process.
              </p>
              <button className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2">
                Learn more
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Feature 3 */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold mb-6">
                Communicate more easily across teams
              </h3>
              <p className="text-xl text-slate-300 mb-6">
                Keep everybody in the loop by centralizing your maintenance and sharing updates automatically across the facility. Allow requesters to track the status of their requests to reduce frustration.
              </p>
              <button className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2">
                Learn more
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-slate-800 rounded-lg p-8 h-96 flex items-center justify-center">
              <MessageSquare className="w-32 h-32 text-amber-500" />
            </div>
          </div>

          {/* Feature 4 */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-slate-800 rounded-lg p-8 h-96 flex items-center justify-center">
              <Calendar className="w-32 h-32 text-amber-500" />
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="text-4xl font-bold mb-6">
                Put preventive maintenance on autopilot
              </h3>
              <p className="text-xl text-slate-300 mb-6">
                Create digital inspection checklists and procedures to enforce regulatory standards and best practices. Schedule recurring inspections to ensure nothing falls through the cracks.
              </p>
              <button className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2">
                Learn more
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Feature 5 */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-4xl font-bold mb-6">
                Streamline audit management with digital work history
              </h3>
              <p className="text-xl text-slate-300 mb-6">
                Comply with OSHA, EPA, FDA and more regulations by mandating certain instructions and gathering a comprehensive digital audit trail automatically.
              </p>
              <button className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2">
                Learn more
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-slate-800 rounded-lg p-8 h-96 flex items-center justify-center">
              <Shield className="w-32 h-32 text-amber-500" />
            </div>
          </div>

          {/* Feature 6 */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-slate-800 rounded-lg p-8 h-96 flex items-center justify-center">
              <TrendingUp className="w-32 h-32 text-amber-500" />
            </div>
            <div className="order-1 lg:order-2">
              <h3 className="text-4xl font-bold mb-6">
                Track your progress with robust reporting
              </h3>
              <p className="text-xl text-slate-300 mb-6">
                Access data-driven insights on work completion, downtime, MTTR and more to drive continuous improvement. Create and share reports to keep your stakeholders informed.
              </p>
              <button className="text-amber-500 hover:text-amber-400 font-medium inline-flex items-center gap-2">
                Learn more
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Pricing that matches checkout
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              ['Free', '$0. Up to 3 users and 100 work orders total.'],
              ['Pro', '$29 per month flat for the company. Unlimited users and work orders.'],
              ['Enterprise', 'Contact us. Checkout does not charge an Enterprise list price.'],
            ].map(([title, copy]) => (
              <div key={title} className="bg-slate-900 border border-slate-800 rounded-lg p-6">
                <h3 className="font-medium text-white mb-3">{title}</h3>
                <p className="text-slate-300">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-amber-500 to-amber-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Get more done with WorkStream Pro
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/signup" 
              className="bg-black hover:bg-slate-900 text-white px-8 py-4 rounded-sm font-bold text-lg"
            >
              Sign up for Free
            </Link>
            <button className="bg-white hover:bg-slate-100 text-black px-8 py-4 rounded-sm font-bold text-lg">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12 px-4">
        <div className="max-w-7xl mx-auto text-center text-slate-400">
          <p>© 2026 WorkStream Pro. All rights reserved.</p>
          <div className="flex flex-wrap justify-center gap-4 mt-4 text-sm">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/cookies" className="hover:text-white">Cookies</Link>
            <a href={SALES_MAILTO} className="hover:text-white">bc2801@hotmail.com</a>
            <a href={FREE_PM_CHECKLISTS_URL} className="hover:text-white">Free PM checklists</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPagePreview;
